/**
 * The one-shot flag that tells the coach's next opener fetch to ask for the
 * payoff sentence instead of the ordinary greeting (Task 10, FR-D6).
 *
 * FirstRun.tsx completes with `navigate('/', { state: { firstRun: true } })`
 * — landing on "/", not "/coach". `Layout` (mounted on every route inside the
 * shell, so it survives the client-side navigation from "/" to "/coach")
 * reads that navigation state once and calls `armFirstRunPayoff()`; whichever
 * `useCoachSession.injectOpener` call runs next — the very next time Coach is
 * opened — calls `consumeFirstRunPayoff()` and appends `?first_run=1`.
 *
 * Module-level state, deliberately not React state or sessionStorage:
 *   - It has to SURVIVE a client-side navigation from "/" to "/coach", which
 *     rules out a value scoped to either route's own component tree.
 *   - It must NOT survive a page reload — the payoff is a moment, not a
 *     banner (see api/coach/prompt.py's first_run_payoff docstring); a
 *     reload of "/" or "/coach" must get the ordinary greeting. A plain
 *     module-level variable gets this for free: the whole JS bundle
 *     re-evaluates on reload, resetting `pending` to `false` — no explicit
 *     cleanup needed. `sessionStorage` would NOT have this property (it
 *     survives a reload within the same tab), which is exactly why it isn't
 *     used here despite living slightly longer than a plain variable would
 *     otherwise seem to need.
 *
 * Known, accepted edge case: reloading "/" after landing there (armed) but
 * before ever opening the coach (consuming) loses the payoff for good —
 * the reload resets `pending` to `false` before anything read it. Left as
 * is, deliberately: FR-D6's own rule is "never a banner", and the failure
 * mode this leans toward is under-showing the payoff on a rare early
 * reload, not over-showing it on an ordinary one. That is the safe side to
 * fail on.
 */
let pending = false;

/** Arm the flag. Called once, by Layout, when it sees `location.state.firstRun`. */
export function armFirstRunPayoff(): void {
  pending = true;
}

/**
 * Consume the flag: returns whether it was armed, and clears it either way —
 * so the second caller in the same page load (a later Coach visit,
 * `startNewChat`, anything else that reads it) always gets `false`, which is
 * what makes "exactly once" true regardless of which call site reaches it
 * first.
 */
export function consumeFirstRunPayoff(): boolean {
  const wasPending = pending;
  pending = false;
  return wasPending;
}
